<template>
    <d-container fluid class="main-content-container px-4">
        <error-modal />

        <d-row no-gutters class="page-header py-4">
            <d-col col sm="4" class="text-center text-sm-left mb-4 d-flex">
                <h3 class="page-title">Dashboard</h3>
            </d-col>

            <d-col lg="8" sm="12" class="d-flex justify-content-end">
                <filter-by-websites
                    :websites="websites"
                    :monthly-sales-data="monthlySalesData"
                    :update-transaction-blocks="updateTransactionBlocks"
                    :reset-dashboard-data="resetDashboardData"
                    :graph="$refs.graph"
                />
            </d-col>
        </d-row>

        <d-row>
            <d-col
                v-for="stats in transactionBlocks"
                xs="12"
                sm="6"
                md="6"
                lg="6"
                xl="3"
                class="mb-4"
            >
                <transaction-blocks
                    :isLoading="isLoading"
                    :chart-data="stats.datasets"
                    :label="stats.label"
                    :amountLabel="stats.amountLabel"
                    :transactionLabel="stats.transactionLabel"
                    :transactionAmount="stats.transactionAmount"
                    :transactionSum="stats.transactionSum"
                    :percentage="stats.percentage"
                    :increase="stats.increase"
                    :decrease="stats.decrease"
                />
            </d-col>
        </d-row>

        <d-row>
            <d-col lg="12" class="mb-4">
                <last-transactions :website-id="websiteId" />
            </d-col>
            <d-col lg="12" class="mb-4">
                <transactions-graph
                    ref="graph"
                    :graph-data="monthlySalesData"
                    :graph-affiliates="graphAffiliates"
                />
            </d-col>
        </d-row>
    </d-container>
</template>

<script>
import {
    WebsiteService,
    SummaryService,
    AffiliateService,
    PerformanceService,
    TransactionService
} from "@/main";
import { BaService } from "@/main";
import performance from "@/mixins/performance";
import TransactionsGraph from "@/components/ecommerce/TransactionsGraph.vue";
import transactionBlocks from "@/components/common/transactionBlocks.vue";
import ErrorModal from "@/components/common/ErrorModal.vue";
import FilterByWebsites from "@/views/FilterButtons.vue";
import notification from "@/mixins/notification";
import loader from "@/mixins/loader";
import DateHelper from "@/helpers/date";
import * as Sentry from "@sentry/browser";

import colors from "../utils/colors";
import LastTransactions from "@/components/transactions/LastTransactions/LastTransactions";

export default {
    mixins: [notification, loader],

    name: "analytics",
    components: {
        LastTransactions,
        ErrorModal,
        FilterByWebsites,
        TransactionsGraph,
        transactionBlocks
    },
    data() {
        return {
            endMonth: DateHelper.getLastDayCurrentMonth(),
            transactionTotal: [],

            todayData: [],
            yesterdayData: [],
            currenMonthData: [],
            lastMonthData: [],

            todayAmount: 0,
            todayCount: 0,

            yesterdayAmount: 0,
            yesterdayCount: 0,

            currentMonthAmount: 0,
            currentMonthCount: 0,

            lastMonthAmount: 0,
            lastMonthCount: 0,

            affiliates: [],
            isLoading: true,

            monthlySalesData: {
                labels: [],
                datasets: []
            },

            graphAffiliates: [],

            websiteId: null,
        };
    },
    methods: {
        async checkIfQuerySet() {
            const code = this.$route.query.code
            const path = this.$route.query.state

            if (code && path) {
                this.$router.push({
                    path: `${path}?code=${code}`
                });
                return;
            }

            if (code) {
                try {
                    await BaService.set(this.$route.query.code);
                    this.showNotification(
                        "success",
                        "Alert",
                        "Microsoft Advertising successfully connected!"
                    );
                    this.$router.push({
                        path: `/network/${this.$cookies.get("website_id")}`
                    });
                } catch (e) {
                    this.showNotification(
                        "warn",
                        "Oops! we have a problem",
                        "Can not connect Microsoft Advertising"
                    );
                    console.log(e);
                }
            }
        },

        async updateTransactionBlocks(
            websiteId,
            summaryToday,
            summaryYesterday,
            summaryCurrentMonth,
            summaryLastMonth
        ) {
            this.websiteId = websiteId // todo use events

            this.todayCount = summaryToday.transactions;
            this.todayAmount = summaryToday.amount;

            this.yesterdayCount = summaryYesterday.transactions;
            this.yesterdayAmount = summaryYesterday.amount;

            this.currentMonthCount = summaryCurrentMonth.transactions;
            this.currentMonthAmount = summaryCurrentMonth.amount;

            this.lastMonthCount = summaryLastMonth.transactions;
            this.lastMonthAmount = summaryLastMonth.amount;

            this.todayData = Object.values(summaryToday.hourly_sum);
            this.yesterdayData = Object.values(summaryYesterday.hourly_sum);
            this.currenMonthData = Object.values(summaryCurrentMonth.daily_sum);
            this.lastMonthData = Object.values(summaryLastMonth.daily_sum);
        },

        async resetDashboardData(affiliateInfo) {
            this.websiteId = null
            this.getSummaryByDate();

            this.graphAffiliates = affiliateInfo;
            this.monthlySalesData.labels = [];
            this.monthlySalesData.datasets = [];

            for (let i = 1; i <= this.endMonth; i++) {
                this.monthlySalesData.labels.push(i);
            }

            this.$refs.graph.update(affiliateInfo);
        },

        async getSummaryByDate() {
            try {
                const summaryToday = await SummaryService.get(
                    DateHelper.getDateToday(),
                    DateHelper.getDateToday(),
                    ""
                );

                const summaryYesterday = await SummaryService.get(
                    DateHelper.getDateYesterday(),
                    DateHelper.getDateYesterday(),
                    ""
                );
                const summaryCurrentMonth = await SummaryService.get(
                    DateHelper.getDateThisMonth(),
                    DateHelper.getMonthEnd(),
                    ""
                );
                const summaryLastMonth = await SummaryService.get(
                    DateHelper.getLastMonthStart(),
                    DateHelper.getLastMonthEnd(),
                    ""
                );

                this.todayCount = summaryToday.transactions;
                this.todayAmount = summaryToday.amount;

                this.yesterdayCount = summaryYesterday.transactions;
                this.yesterdayAmount = summaryYesterday.amount;

                this.currentMonthCount = summaryCurrentMonth.transactions;
                this.currentMonthAmount = summaryCurrentMonth.amount;

                this.lastMonthCount = summaryLastMonth.transactions;
                this.lastMonthAmount = summaryLastMonth.amount;

                this.todayData = Object.values(summaryToday.hourly_sum);
                this.yesterdayData = Object.values(summaryYesterday.hourly_sum);
                this.currenMonthData = Object.values(summaryCurrentMonth.daily_sum);
                this.lastMonthData = Object.values(summaryLastMonth.daily_sum);

                this.isLoading = false;
            } catch(err) {
                this.isLoading = false;
                Sentry.captureException(err)
                Sentry.captureMessage('Summary service get error')
            }
        },
    },

    computed: {
        websites() {
            return this.$store.getters.userInfo.websites;
        },

        transactionBlocks() {
            return [
                {
                    label: "Today",
                    transactionLabel: "Transactions",
                    amountLabel: "Total amount",
                    transactionAmount: this.todayCount,
                    transactionSum: this.todayAmount.toFixed(2),
                    datasets: [
                        {
                            label: "Today",
                            fill: "start",
                            borderWidth: 1.5,
                            backgroundColor: colors.primary.toRGBA(0.1),
                            borderColor: colors.primary.toRGBA(),
                            data: this.todayData
                        }
                    ]
                },
                {
                    label: "Yesterday",
                    amountLabel: "Total amount",
                    transactionAmount: this.yesterdayCount,
                    transactionSum: this.yesterdayAmount.toFixed(2),
                    transactionLabel: "Transactions",
                    datasets: [
                        {
                            label: "Today",
                            fill: "start",
                            borderWidth: 1.5,
                            backgroundColor: colors.success.toRGBA(0.1),
                            borderColor: colors.success.toRGBA(),
                            data: this.yesterdayData
                        }
                    ]
                },
                {
                    label: "This month",
                    transactionLabel: "Transactions",
                    amountLabel: "Total amount",
                    transactionAmount: this.currentMonthCount,
                    transactionSum: this.currentMonthAmount.toFixed(2),
                    datasets: [
                        {
                            label: "Today",
                            fill: "start",
                            borderWidth: 1.5,
                            backgroundColor: colors.warning.toRGBA(0.1),
                            borderColor: colors.warning.toRGBA(),
                            data: this.currenMonthData
                        }
                    ]
                },
                {
                    label: "Last month",
                    transactionLabel: "Transactions",
                    amountLabel: "Total amount",
                    transactionAmount: this.lastMonthCount,
                    transactionSum: this.lastMonthAmount.toFixed(2),
                    datasets: [
                        {
                            label: "Today",
                            fill: "start",
                            borderWidth: 1.5,
                            backgroundColor: colors.salmon.toRGBA(0.1),
                            borderColor: colors.salmon.toRGBA(),
                            data: this.lastMonthData
                        }
                    ]
                }
            ];
        }
    },

    created() {
        this.getSummaryByDate();
        this.checkIfQuerySet();
    }
};
</script>

<style>
.show-add-data-button {
    width: 30px;
    height: 30px;
    margin-left: 5px;
    background-color: transparent;
    outline: none;
    border: none;
}
</style>
